/**
 * @description Formatea una duración en milisegundos a una cadena legible.
 * Ejemplos: "2d 4h 30m", "3h 15m", "45m", "< 1m"
 * @param {number} ms - Duración en milisegundos.
 * @returns {string} Cadena formateada.
 */
const formatearDuracion = (ms) => {
  if (ms < 0) ms = 0;

  const minutos = Math.floor(ms / 60000);
  const horas = Math.floor(minutos / 60);
  const dias = Math.floor(horas / 24);

  if (minutos < 1) return '< 1m';

  const partes = [];
  if (dias > 0) partes.push(`${dias}d`);
  if (horas % 24 > 0) partes.push(`${horas % 24}h`);
  if (minutos % 60 > 0) partes.push(`${minutos % 60}m`);

  return partes.join(' ');
};

/**
 * @description Calcula el tiempo transcurrido de una orden de trabajo.
 *
 * Lógica por estado:
 * - COMPLETED / CANCELED: tiempo desde creación hasta closed_at.
 *   Si closed_at es null (registros anteriores), usa updatedAt como aproximación.
 * - Otros estados activos: tiempo desde creación hasta ahora (en curso).
 *
 * @param {string | null} createdAt - Fecha de creación de la OT (ISO string).
 * @param {string | null} closedAt - Fecha de cierre de la OT (ISO string o null).
 * @param {string | null} updatedAt - Última actualización (ISO string), usado como fallback.
 * @param {string} status - Estado actual de la OT (OrderStatus enum).
 * @returns {{ duracion: string, estaEnCurso: boolean, etiqueta: string }}
 */
export const calcularTiempoTranscurrido = (createdAt, closedAt, updatedAt, status) => {
  if (!createdAt) {
    return { duracion: '—', estaEnCurso: false, etiqueta: 'Duración' };
  }

  const inicio = new Date(createdAt).getTime();
  const estadosTerminales = ['COMPLETED', 'CANCELED'];
  const estaEnCurso = !estadosTerminales.includes(status);

  let fin;
  let etiqueta;

  if (estaEnCurso) {
    // OT activa: calcular desde creación hasta ahora
    fin = Date.now();
    etiqueta = 'Tiempo en curso';
  } else if (closedAt) {
    // OT cerrada con fecha de cierre registrada
    fin = new Date(closedAt).getTime();
    etiqueta = status === 'COMPLETED' ? 'Tiempo total' : 'Tiempo hasta cancelación';
  } else if (updatedAt) {
    // Fallback: registros anteriores sin closed_at — usar updatedAt como aproximación
    fin = new Date(updatedAt).getTime();
    etiqueta = 'Duración (aprox.)';
  } else {
    return { duracion: '—', estaEnCurso: false, etiqueta: 'Duración' };
  }

  const duracion = formatearDuracion(fin - inicio);
  return { duracion, estaEnCurso, etiqueta };
};
